import styled from 'styled-components';
import 'bootstrap/dist/css/bootstrap.min.css';
import { TagBadge, TagBadgeData } from './tag-badge';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { CurrentTagContent } from './current-tag-content';
import { useEffect, useRef, useState } from 'react';
import { HomePixiView } from './home-pixi-view';
import { RoleId } from './role-id';
import FontStyles from '../font/font';
import { FirstVisitView } from './first-visit-view';
import { getCookies, setCookies } from '../cookies-manager';
import { LoaderScreen } from '../loader/loader-screen';
import { trackPromises } from '../loader/track-promise';
import MenuButton from './menu-button';
import MenuScreen, { MenuScreenHandle } from '@root/options-card-menu/menu-screen';
import bloodTexture from '../assets/blood.png';

const S = {
  Container: styled.main`
    height: 100%;
    width: 100%;
    margin: auto;
    overflow: hidden;
    background-color: #008081;
    position: relative;
  `,

  Window: styled.div`
    position: absolute;
    top: 49%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: 30rem;
    width: calc(30rem * (4 / 3));
    font-family: 'Dogica', sans-serif;
    word-spacing: -0.5ch;
  `,

  Presentation: styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    background: rgb(0, 119, 167);
    background: linear-gradient(0deg, rgb(0, 119, 167) 0%, rgb(0, 119, 167) 10%, rgb(233, 253, 255) 50%);
    border: 0.15rem solid rgb(255, 255, 255);
    border-color: #fff #000 #000 #fff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `,
  TopBar: styled.div`
    position: absolute;
    top: calc(-2rem + 0.15rem);
    width: 100%;
    font-size: 0.8rem;
    padding: 0.3rem 0.7rem;
    background-color: #0000a8;
    border: 0.15rem solid rgb(255, 255, 255);
    border-color: #fff #000 #000 #fff;
  `,
  PixiContainer: styled.div`
    height: 90%;
    width: 100%;
  `,
  DialogueBar: styled.div`
    position: absolute;
    bottom: 0;
    padding: 1rem 1.5rem 1.5rem 1.5rem;
    width: 95%;
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 1rem;
    border: 0.2rem solid #54b4d3;
    color: black;
    font-size: 0.96rem;
  `,

  RoleContainer: styled.div`
    display: flex;
    justify-content: center;
  `,
  RoleTagContent: styled.div`
    margin-top: 0.5rem;
    display: flex;
    justify-content: center;
    gap: 0.4rem;
  `,
  SelectedTagBadgeContent: styled.div`
    margin-top: -0.1rem;
    padding-bottom: 0.1rem;
  `,

  BloodImage: styled.div`
    position: absolute;
    top: -5%;
    left: 60%;
    transform: translateX(-50%);
    height: 60rem;
    width: 80rem;
    background-image: url(${bloodTexture});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    pointer-events: none;
  `,
};

export const Home = () => {
  const [selectedTagBadgeId, setSelectedTagBadgeId] = useState<RoleId>('dev');
  const pixiContainerRef = useRef<HTMLDivElement>(null);
  const [pixiView, setPixiView] = useState<HomePixiView | undefined>(undefined);
  const [firstVisit, setFirstVisit] = useState(
    getCookies('isVisited') === undefined || getCookies('isVisited') === 'false'
  );
  const [homeDownloadProgress, setHomeDownloadProgress] = useState(getCookies('homeDownloadProgress'));
  const [loadProgress, setLoadProgress] = useState(0);
  const [startDragTime, setStartDragTime] = useState<number>(0);
  const menuScreenRef = useRef<MenuScreenHandle>(null);

  const badgeDatas: TagBadgeData[] = [
    { id: 'dev', text: 'Game Dev', background: 'info', textColor: 'light' },
    { id: 'game-designer', text: 'Game Designer', background: 'info', textColor: 'light' },
    { id: 'animator', text: '2D Animator', background: 'info', textColor: 'light' },
    { id: 'artist', text: 'Artist', background: 'info', textColor: 'light' },
    { id: 'cooker', text: 'Cooker', background: 'info', textColor: 'light' },
  ];

  const [selectedBadges, setSelectedBadges] = useState<RoleId[]>([]);

  const handleLaunch = () => {
    setFirstVisit(false);
    setCookies('isVisited', 'true');

    setDownloadProgress('dev');
  };

  const setDownloadProgress = (selectedId: RoleId) => {
    if (homeDownloadProgress === '100') return;

    const badges = [...selectedBadges];
    if (!badges.includes(selectedId)) {
      badges.push(selectedId);
      setSelectedBadges(badges);
    }
    const newProgress = Math.min(100, Math.round(100 / badgeDatas.length) * badges.length).toString();
    setHomeDownloadProgress(newProgress);
    setCookies('homeDownloadProgress', newProgress);

    // console.log(homeDownloadProgress + '%');
  };

  useEffect(() => {
    if (!pixiContainerRef.current) return;

    if (pixiView) pixiView.dispose();
    setPixiView(new HomePixiView(pixiContainerRef.current!));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixiContainerRef, firstVisit]);

  useEffect(() => {
    if (!pixiView) return;
    trackPromises([pixiView.initialize()], setLoadProgress);
  }, [pixiView]);

  function handleDragStart() {
    setStartDragTime(Date.now());
  }

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;

    if (Date.now() - startDragTime > 200 && !over) return;

    if (selectedTagBadgeId !== active.id) {
      const activeId = active.id as RoleId;
      setSelectedTagBadgeId(activeId);
      pixiView?.switchSkin(activeId);
      setDownloadProgress(activeId);
    }
  }

  return (
    <S.Container>
      <FontStyles />
      {firstVisit && <FirstVisitView onLaunch={handleLaunch} />}
      {!firstVisit && (
        <MenuButton
          downloadProgress={homeDownloadProgress ? parseInt(homeDownloadProgress) : 0}
          onClick={() => {
            menuScreenRef.current?.open();
          }}
        />
      )}
      {!firstVisit && (
        <S.Window>
          <S.TopBar>[{selectedTagBadgeId}] SIJIA.exe</S.TopBar>
          <S.Presentation>
            <LoaderScreen loaderType="home" progress={loadProgress} />
            <S.PixiContainer ref={pixiContainerRef} />
            <S.DialogueBar>
              <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <S.RoleContainer>
                  <div>Hi! I'm SIJIA WANG! I'm&nbsp;</div>
                  <CurrentTagContent>
                    <S.SelectedTagBadgeContent>
                      <TagBadge
                        id={selectedTagBadgeId}
                        text={badgeDatas.find((dt) => dt.id === selectedTagBadgeId)!.text}
                        background={
                          homeDownloadProgress === '100' || selectedBadges.includes(selectedTagBadgeId)
                            ? 'success'
                            : 'info'
                        }
                        textColor={badgeDatas.find((dt) => dt.id === selectedTagBadgeId)!.textColor}
                        draggable={false}
                      />
                    </S.SelectedTagBadgeContent>
                  </CurrentTagContent>
                </S.RoleContainer>

                <S.RoleTagContent>
                  {badgeDatas
                    .filter((dt) => dt.id != selectedTagBadgeId)
                    .map((badgeData) => (
                      <TagBadge
                        key={badgeData.id}
                        id={badgeData.id}
                        text={badgeData.text}
                        background={
                          homeDownloadProgress === '100' || selectedBadges.includes(badgeData.id) ? 'success' : 'info'
                        }
                        textColor={badgeData.textColor}
                      />
                    ))}
                </S.RoleTagContent>
              </DndContext>
            </S.DialogueBar>
          </S.Presentation>
        </S.Window>
      )}
      {getCookies('shutDown') === 'true' && <S.BloodImage />}
      {!firstVisit && <MenuScreen optionTitle={'Home'} ref={menuScreenRef} />}
    </S.Container>
  );
};
